/**
 * This file is responsible for rendering Forge Custom UI components.
 * See https://developer.atlassian.com/platform/forge/custom-ui/ for more details.
 */

/**
 * EXT-2375: FILE MARKED FOR DELETION
 * after Statisg Gate 'forge-ui-use-consolidated-renderer' is cleaned up
 */
import React, { FC, useCallback, useState } from 'react';

import { useApolloClient } from '@apollo/react-hooks';

import ModalDialog, {
  ModalTransition,
  OnCloseHandler,
  ModalBody,
} from '@atlaskit/modal-dialog';
import { Extension } from '@atlassian/forge-ui';
import { CoreData } from '@atlassian/forge-ui-types';
import { Iframe, getModalDimensions } from '@atlassian/forge-ui/iframe';

import { User } from 'src/components/types';

import { BridgeType, ForgeUIExtensionDataType } from '../../types';
import { getEnvironment } from '../../utils/environment';

import { HostedResourceWrapper } from './HostedResourceWrapper';
import { ModalWrapper } from './styled';

export interface IframeRendererProps {
  onInitialRender?: () => void;
  components?: {
    [props: string]: Function;
  };
  onTearDown?: () => void;
  app: Extension;
  contextIds: string[];
  coreData: CoreData;
  currentUser?: User;
  extensionData: ForgeUIExtensionDataType;
  bridge?: BridgeType;
  getContextToken?: () => Promise<string>;
  locale?: string;
}

interface ModalState {
  closeOnEscape: boolean;
  closeOnOverlayClick: boolean;
  context: object;
  onClose: (payload: any) => void;
  requiresUserConsent: boolean;
  resource: string;
  size: Parameters<typeof getModalDimensions>[0];
}

export const ForgeIframeRenderer: FC<IframeRendererProps> = ({
  app,
  contextIds,
  coreData,
  currentUser,
  extensionData,
  components,
  onInitialRender,
  bridge,
  getContextToken,
  locale,
}: IframeRendererProps) => {
  const { close, ...bridgeProps } = bridge || {};
  const [modalState, setModalState] = useState<undefined | ModalState>(
    undefined
  );

  const { height: modalHeight, minHeight: modalMinHeight } = getModalDimensions(
    modalState?.size || 'medium'
  );
  const akModalSize =
    modalState?.size === 'xlarge' ? 'x-large' : modalState?.size;

  const onClose = (payload?: { data: any }) => {
    if (modalState && modalState.onClose) {
      modalState.onClose(payload?.data);
      setModalState(undefined);
    }
  };

  const getComponents = useCallback(
    defaultComponents => ({
      ...defaultComponents,
      ...(components || []),
    }),
    [components]
  );

  const apolloClient = useApolloClient();

  const iframeProps = {
    locale,
    accountId: currentUser?.account_id,
    apolloClient,
    components: getComponents,
    contextIds,
    coreData,
    getContextToken,
  };

  return (
    <HostedResourceWrapper
      onInitialRender={onInitialRender}
      environment={getEnvironment()}
    >
      <Iframe
        {...iframeProps}
        extension={app}
        extensionData={extensionData}
        isResizable
        bridge={{
          ...bridgeProps,
          close,
          openModal: (opts, consentState) => {
            setModalState({
              ...opts.data,
              requiresUserConsent: Boolean(consentState?.requiresUserConsent),
            });
            return true;
          },
        }}
      />
      <ModalTransition>
        {modalState && (
          <ModalDialog
            onClose={onClose as unknown as OnCloseHandler} // bridge enables passing custom payload into this function so the typing changes
            shouldCloseOnEscapePress={modalState.closeOnEscape}
            shouldCloseOnOverlayClick={modalState.closeOnOverlayClick}
            shouldScrollInViewport
            width={akModalSize}
          >
            <ModalBody>
              <ModalWrapper
                modalHeight={modalHeight}
                modalMinHeight={modalMinHeight}
              >
                <Iframe
                  {...iframeProps}
                  // overriding just 2 values which are the resource value which points to the modal resource
                  // defined in manifest and the requiresUserConsent defined specifically for modal
                  extension={{
                    ...app,
                    properties: {
                      ...app.properties,
                      resource: modalState.resource,
                    },
                    requiresUserConsent: modalState.requiresUserConsent,
                  }}
                  // add the modal specific context passed on from the app to existing extension data
                  extensionData={{
                    ...extensionData,
                    modal: modalState.context,
                  }}
                  bridge={{
                    ...bridgeProps,
                    close: onClose,
                  }}
                  height="100%"
                  width="100%"
                />
              </ModalWrapper>
            </ModalBody>
          </ModalDialog>
        )}
      </ModalTransition>
    </HostedResourceWrapper>
  );
};
